import { InitData, assetUrl } from '../../core'
import { t as i18n } from '../../core/i18n'

interface Props {
  initData: InitData
  title?: string | null
  maxItems?: number | null
}

/**
 * TestimonialsWidget — mirrors HS-native testimonial styling.
 *
 * Card layout:
 *   - Big left-side orange quote-mark accent
 *   - Italic quote text (the testimonial)
 *   - Optional star rating row (orange filled, muted empty)
 *   - Author block: avatar (image or initial circle) + name + subtitle
 *   - White card, 1px var(--yoga-card-border), 2rem padding
 *   - Auto-fit grid (1 col on narrow, 2 col on wider)
 *
 * Data comes from initData.testimonials — no separate API call.
 * initData is sync, so undefined and [] are treated identically: empty state.
 */
export function TestimonialsWidget({ initData, title = null, maxItems = null }: Props) {
  // Alias `i18n` because the render below shadows `t` with its map parameter.
  const str = i18n(initData.locale)
  const all = initData.testimonials || []
  const testimonials = maxItems && maxItems > 0 ? all.slice(0, maxItems) : all

  if (testimonials.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💬</div>
        <p>{str.noTestimonials}</p>
      </div>
    )
  }

  return (
    <div>
      <style>{testimonialStyles}</style>
      {title && <h2 class="hsw-testimonial-title">{title}</h2>}
      <div class="hsw-testimonial-grid">
        {testimonials.map((t, i) => (
          <div key={i} class="hsw-testimonial-card">
            <span class="hsw-quote-mark" aria-hidden="true">&ldquo;</span>

            {t.rating && t.rating > 0 && (
              <div class="hsw-rating" aria-label={str.starsOutOf.replace('{n}', String(Math.round(t.rating)))}>
                {[1, 2, 3, 4, 5].map(n => (
                  <span
                    key={n}
                    class={n <= Math.round(t.rating ?? 0) ? 'hsw-star hsw-star-on' : 'hsw-star'}
                    aria-hidden="true"
                  >
                    ★
                  </span>
                ))}
              </div>
            )}

            <p class="hsw-testimonial-text">{t.text}</p>

            <div class="hsw-testimonial-author">
              {t.image ? (
                <img src={assetUrl(t.image)} alt={t.name} class="hsw-avatar-img" />
              ) : (
                <div class="hsw-avatar-fallback" aria-hidden="true">
                  {t.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div class="hsw-author-meta">
                <div class="hsw-author-name">{t.name}</div>
                {t.subtitle && <div class="hsw-author-sub">{t.subtitle}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const testimonialStyles = `
  .hsw-testimonial-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 1100px;
    letter-spacing: -0.01em;
  }
  .hsw-testimonial-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.5rem;
    max-width: 1100px;
    margin: 0 auto;
  }
  .hsw-testimonial-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 2rem;
    position: relative;
    display: flex;
    flex-direction: column;
    transition: all .3s;
  }
  .hsw-testimonial-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .hsw-quote-mark {
    position: absolute;
    top: 0.5rem;
    left: 1.25rem;
    font-size: 4.5rem;
    line-height: 1;
    color: var(--yoga-primary, #FF6B35);
    font-family: Georgia, 'Times New Roman', serif;
    font-weight: 700;
    opacity: 0.85;
    pointer-events: none;
    user-select: none;
  }
  .hsw-rating {
    display: flex;
    gap: 2px;
    margin: 0 0 1rem;
    position: relative;
    z-index: 1;
  }
  .hsw-star {
    font-size: 1rem;
    color: var(--yoga-card-border, #e5e7eb);
    line-height: 1;
  }
  .hsw-star-on {
    color: var(--yoga-primary, #FF6B35);
  }
  .hsw-testimonial-text {
    font-style: italic;
    font-size: 1rem;
    line-height: 1.65;
    color: var(--yoga-text, #404040);
    margin: 0 0 1.5rem;
    position: relative;
    z-index: 1;
    flex: 1;
  }
  .hsw-testimonial-author {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid var(--yoga-card-border, #e5e7eb);
  }
  .hsw-avatar-img,
  .hsw-avatar-fallback {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .hsw-avatar-img {
    object-fit: cover;
  }
  .hsw-avatar-fallback {
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1.05rem;
    letter-spacing: -0.01em;
  }
  .hsw-author-meta {
    min-width: 0;
  }
  .hsw-author-name {
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--yoga-text, #0a0a0a);
    line-height: 1.2;
  }
  .hsw-author-sub {
    font-size: 0.8rem;
    color: var(--yoga-text-muted, #737373);
    margin-top: 2px;
    line-height: 1.3;
  }
`
